import type {
  AllocationPreviewView,
  BillingAccessPermission,
  ChargeKind,
  ChargeView,
  FeeConcessionKind,
  FeeRunOutcome,
  FeeRunSkipReason,
  PaymentMethod,
  PaymentView,
  StatementEntryKindName,
  SupportedLocale,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

/** Every currency the ledger supports has two minor-unit digits (see `currencyExponents` in `@stm/domain`). */
const minorUnitDigits = 2;

export const paymentMethodKeys: Readonly<Record<PaymentMethod, TranslationKey>> = {
  cash: 'billing.method.cash',
  bank_transfer: 'billing.method.bankTransfer',
  card_terminal: 'billing.method.cardTerminal',
  opening_balance: 'billing.method.openingBalance',
  other: 'billing.method.other',
};

export const accessPermissions: readonly BillingAccessPermission[] = ['view', 'manage'];

export const accessPermissionKeys: Readonly<Record<BillingAccessPermission, TranslationKey>> = {
  view: 'billing.access.permission.view',
  manage: 'billing.access.permission.manage',
};

export const chargeKindKeys: Readonly<Record<ChargeKind, TranslationKey>> = {
  fee: 'billing.kind.fee',
  manual: 'billing.kind.manual',
  opening: 'billing.kind.opening',
};

export const statementKindKeys: Readonly<Record<StatementEntryKindName, TranslationKey>> = {
  charge: 'billing.statement.kind.charge',
  credit_note: 'billing.statement.kind.creditNote',
  payment: 'billing.statement.kind.payment',
  payment_reversal: 'billing.statement.kind.paymentReversal',
  refund: 'billing.statement.kind.refund',
};

/** Methods finance can pick when posting a payment; opening balances come from the finance import. */
export const postablePaymentMethods: readonly PaymentMethod[] = [
  'cash',
  'bank_transfer',
  'card_terminal',
  'other',
];

/**
 * Converts what someone typed ("200", "200.5", "1 200,50") into minor units. Both decimal
 * separators are accepted, at most two decimals, and only a positive amount is valid; anything else
 * is null so the form can show a message instead of guessing.
 */
export function parseAmountInput(text: string): string | null {
  const compact = text.replace(/\s/g, '');
  const match = /^(\d{1,16})(?:[.,](\d{1,2}))?$/.exec(compact);
  if (match === null) return null;
  const major = match[1] ?? '';
  const fraction = (match[2] ?? '').padEnd(minorUnitDigits, '0');
  const minor = BigInt(major) * 10n ** BigInt(minorUnitDigits) + BigInt(fraction);
  return minor > 0n ? minor.toString() : null;
}

function decimalSeparator(locale: SupportedLocale): string {
  return (
    new Intl.NumberFormat(locale).formatToParts(1.1).find((part) => part.type === 'decimal')
      ?.value ?? '.'
  );
}

/** An exact amount: integer arithmetic on the minor units, never a floating point division. */
export function formatMoney(minorUnits: string, currency: string, locale: SupportedLocale): string {
  const amount = BigInt(minorUnits);
  const negative = amount < 0n;
  const absolute = negative ? -amount : amount;
  const scale = 10n ** BigInt(minorUnitDigits);
  const major = new Intl.NumberFormat(locale).format(absolute / scale);
  const fraction = (absolute % scale).toString().padStart(minorUnitDigits, '0');
  return `${negative ? '−' : ''}${major}${decimalSeparator(locale)}${fraction} ${currency}`;
}

/** An amount as it is typed into a field (no grouping, a dot), for prefilling editable lines. */
export function amountInputText(minorUnits: string): string {
  const amount = BigInt(minorUnits);
  const scale = 10n ** BigInt(minorUnitDigits);
  return `${(amount / scale).toString()}.${(amount % scale).toString().padStart(minorUnitDigits, '0')}`;
}

export type ChargeStanding = 'reversed' | 'paid' | 'open';

export function chargeStanding(
  charge: Pick<ChargeView, 'reversed' | 'outstandingMinor'>,
): ChargeStanding {
  if (charge.reversed) return 'reversed';
  return BigInt(charge.outstandingMinor) === 0n ? 'paid' : 'open';
}

export const chargeStandingKeys: Readonly<Record<ChargeStanding, TranslationKey>> = {
  reversed: 'billing.charge.reversed',
  paid: 'billing.charge.paid',
  open: 'billing.charge.open',
};

/** Credit notes and reversals apply to a charge that is neither reversed nor already cleared. */
export function canAdjustCharge(
  charge: Pick<ChargeView, 'reversed' | 'outstandingMinor'>,
): boolean {
  return chargeStanding(charge) === 'open';
}

export function canReverseCharge(charge: Pick<ChargeView, 'reversed'>): boolean {
  return !charge.reversed;
}

export function canReversePayment(
  payment: Pick<PaymentView, 'reversed' | 'refundedMinor'>,
): boolean {
  return !payment.reversed && BigInt(payment.refundedMinor) === 0n;
}

export function canRefundPayment(
  payment: Pick<PaymentView, 'reversed' | 'availableMinor'>,
): boolean {
  return !payment.reversed && BigInt(payment.availableMinor) > 0n;
}

/** Credit that is still available can go to an open charge, so the button needs both. */
export function canAllocateCredit(
  payment: Pick<PaymentView, 'reversed' | 'availableMinor'>,
  charges: readonly Pick<ChargeView, 'reversed' | 'outstandingMinor'>[],
): boolean {
  return canRefundPayment(payment) && charges.some((charge) => canAdjustCharge(charge));
}

/** One editable allocation line: what the person typed against an open charge. */
export interface AllocationDraftLine {
  chargeId: string;
  amountText: string;
}

/** Starts the editable lines from the server's oldest-due-first preview. */
export function draftLinesFromPreview(preview: AllocationPreviewView): AllocationDraftLine[] {
  return preview.lines.map((line) => ({
    chargeId: line.chargeId,
    amountText: amountInputText(line.amountMinor),
  }));
}

export type AllocationDraftProblem = 'invalidAmount' | 'exceedsPayment';

export interface AllocationDraftResult {
  lines: { chargeId: string; amountMinor: string }[];
  /** Total of the lines in minor units. */
  allocatedMinor: bigint;
  /** What stays on the account as unallocated credit. */
  unallocatedMinor: bigint;
  problem: AllocationDraftProblem | null;
}

/**
 * Reads the editable lines against the payment amount. Empty lines are dropped; a line that is not
 * a positive amount, or lines that together exceed the payment, are reported instead of sent. The
 * server repeats every check (outstanding amounts, foreign charges) when the payment is posted.
 */
export function evaluateAllocationDraft(
  paymentMinor: string,
  draft: readonly AllocationDraftLine[],
): AllocationDraftResult {
  const payment = BigInt(paymentMinor);
  const lines: { chargeId: string; amountMinor: string }[] = [];
  let allocated = 0n;
  let problem: AllocationDraftProblem | null = null;
  for (const line of draft) {
    if (line.amountText.trim() === '') continue;
    const amountMinor = parseAmountInput(line.amountText);
    if (amountMinor === null) {
      problem = 'invalidAmount';
      continue;
    }
    lines.push({ chargeId: line.chargeId, amountMinor });
    allocated += BigInt(amountMinor);
  }
  if (problem === null && allocated > payment) problem = 'exceedsPayment';
  return {
    lines,
    allocatedMinor: allocated,
    unallocatedMinor: payment - allocated,
    problem,
  };
}

export const allocationProblemKeys: Readonly<Record<AllocationDraftProblem, TranslationKey>> = {
  invalidAmount: 'billing.payment.allocation.invalidAmount',
  exceedsPayment: 'billing.payment.allocation.exceedsPayment',
};

export const concessionKindKeys: Readonly<Record<FeeConcessionKind, TranslationKey>> = {
  none: 'billing.fees.concession.none',
  amount: 'billing.fees.concession.amount',
  percent: 'billing.fees.concession.percent',
};

export const feeRunOutcomeKeys: Readonly<Record<FeeRunOutcome, TranslationKey>> = {
  generate: 'billing.fees.run.outcome.generate',
  review_first_charge: 'billing.fees.run.outcome.review',
  already_billed: 'billing.fees.run.outcome.billed',
  skip: 'billing.fees.run.outcome.skip',
};

export const feeRunSkipReasonKeys: Readonly<Record<FeeRunSkipReason, TranslationKey>> = {
  not_started: 'billing.fees.run.skip.notStarted',
  ended: 'billing.fees.run.skip.ended',
  paused: 'billing.fees.run.skip.paused',
  plan_not_effective: 'billing.fees.run.skip.planNotEffective',
  fully_waived: 'billing.fees.run.skip.fullyWaived',
  account_closed: 'billing.fees.run.skip.accountClosed',
  period_closed: 'billing.fees.run.skip.periodClosed',
};

/**
 * Reads a typed percentage ("25", "12,5") into basis points (1 = 0.01 %). Both decimal separators
 * are accepted, at most two decimals, from 0.01 % to 100 %; anything else is null.
 */
export function parsePercentInput(text: string): string | null {
  const match = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(text.replace(/\s/g, ''));
  if (match === null) return null;
  const basisPoints = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return basisPoints >= 1 && basisPoints <= 10000 ? String(basisPoints) : null;
}

/** Basis points as a percentage in the reader's number format, without a floating point division. */
export function formatBasisPoints(basisPoints: string, locale: SupportedLocale): string {
  const value = Number(basisPoints);
  const whole = Math.trunc(value / 100);
  const fraction = value % 100;
  if (fraction === 0) return `${String(whole)} %`;
  const digits = fraction.toString().padStart(2, '0').replace(/0$/, '');
  return `${String(whole)}${decimalSeparator(locale)}${digits} %`;
}

/** The year and month of a `YYYY-MM` month field, or null when it is not a real month. */
export function parseMonthInput(text: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(text);
  if (match === null) return null;
  const year = Number(match[1]);
  return year >= 2000 && year <= 2100 ? { year, month: Number(match[2]) } : null;
}

/** The day before an ISO calendar date, the latest day a period can be closed through. */
export function previousDay(isoDate: string): string {
  const day = new Date(`${isoDate}T00:00:00.000Z`);
  day.setUTCDate(day.getUTCDate() - 1);
  return day.toISOString().slice(0, 10);
}
